import { BarChart } from "@mantine/charts"
import { Box, Flex, Slider, ThemeIcon, Tooltip } from "@mantine/core"
import { IconHelpCircle } from "@tabler/icons-react"
import _ from "lodash"
import { useEffect, useState } from "react"

import { Filters } from "@costory/types/filters"

import { isGroupByOptional } from "@costory/shared/utils/filters"
import { formatNumber } from "@costory/shared/utils/format"

import WaterfallChartTooltip from "@costory/front/components/charts/waterfallChartTooltip"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useWaterfallQuery } from "@costory/front/queries/explore"
import {
  COLOR_PALETTE,
  PayloadItem,
  truncateLabel,
} from "@costory/front/utils/charts"
import { useYAxisLabelConfig } from "@costory/front/utils/format"

type Props = {
  filters: Filters
  embed?: boolean
  setThreshold?: (value: number | null) => void
  height?: number
  withLegend?: boolean
  drillDownInto?: (value: string, mode: "sameWindow" | "newTab") => void
  tooltipYPosition?: number
  dashboardChartConfig?: number
  withTooltip?: boolean
  isCostCard?: boolean
  isAnimationActive?: boolean
}

const getBarColor = (cost: number, index: number, size: number): string => {
  if (index === 0 || index === size - 1) return "primaryHighlight"
  return cost > 0 ? "red.5" : "green.5"
}

export const WaterfallChart = ({
  filters,
  height,
  withLegend,
  drillDownInto,
  setThreshold,
  embed,
  tooltipYPosition = 0,
  dashboardChartConfig,
  withTooltip = true,
  isCostCard = false,
  isAnimationActive = true,
}: Props) => {
  const waterfallQuery = useWaterfallQuery(filters)
  const [thresholdForViz, setThresholdForViz] = useState(filters.threshold || 0)

  useEffect(() => {
    setThresholdForViz(filters.threshold || 0)
  }, [filters.threshold])

  const sliderLabelFactory = (value: number) => `${filters.currency} ${value}`
  const yAxisLabel = useYAxisLabelConfig(filters.metricId, filters.currency)
  const yAxisLabelProps = filters.metricId
    ? { ...yAxisLabel, offset: 0 }
    : undefined

  const groupByOptional = isGroupByOptional(filters.groupBy)
  return (
    <QueryWrapper query={waterfallQuery}>
      {({ data }) => (
        <>
          {setThreshold && (
            <Flex align="center" gap={15} justify="right" mb="lg">
              <Tooltip
                w={400}
                multiline={true}
                label="Combine all items with an absolute value below this threshold."
              >
                <ThemeIcon
                  style={{ cursor: "pointer" }}
                  c="primaryHighlight"
                  size="sm"
                >
                  <IconHelpCircle />
                </ThemeIcon>
              </Tooltip>
              <Box w={200}>
                <Slider
                  max={1000}
                  step={250}
                  restrictToMarks
                  labelAlwaysOn
                  label={(value) => sliderLabelFactory(value)}
                  value={thresholdForViz}
                  onChange={(value) => {
                    setThresholdForViz(value)
                  }}
                  onChangeEnd={(value) => {
                    setThreshold(value || null)
                  }}
                />
              </Box>
            </Flex>
          )}
          <BarChart
            h={height ?? "100%"}
            pr={isCostCard ? "5px" : (dashboardChartConfig ?? "30px")}
            pl={isCostCard ? "-10px" : "8px"}
            pt={isCostCard ? "10px" : ""}
            data={_.filter(
              data.waterfallChartData.map((elem, index) => ({
                cost: elem.cost,
                name: isCostCard
                  ? truncateLabel(elem.name, 8)
                  : truncateLabel(elem.name, 12),
                fullName: elem.name,
                color: getBarColor(
                  elem.cost,
                  index,
                  data.waterfallChartData.length,
                ),
              })),
              (elem) =>
                Math.abs(elem.cost) >= 0.01 ||
                elem.fullName === "Previous" ||
                elem.fullName === "Current",
            )}
            xAxisProps={{
              interval: 0,
              angle: 30,
              textAnchor: "start",
              height: isCostCard ? 40 : (dashboardChartConfig ?? undefined),
              style: {
                fontSize: isCostCard ? "8px" : embed ? "16px" : "12px",
              },
            }}
            yAxisProps={{
              allowDataOverflow: true,
              label: isCostCard ? "" : yAxisLabelProps,
            }}
            dataKey="name"
            type="waterfall"
            tooltipProps={
              withTooltip
                ? {
                    position: { y: tooltipYPosition },
                    content: ({ payload }) => (
                      <WaterfallChartTooltip
                        label={payload?.[0]?.payload?.fullName ?? ""}
                        payload={payload as PayloadItem[]}
                        tooltipData={data.dataGroupedByValueWithOthers}
                        currency={filters.currency}
                        groupByOptional={groupByOptional}
                      />
                    ),
                  }
                : { content: () => null }
            }
            valueFormatter={(value) =>
              formatNumber({ value, currency: filters.currency })
            }
            series={[{ name: "cost", color: COLOR_PALETTE[0] }]}
            withLegend={withLegend && !groupByOptional}
            barChartProps={{
              onClick: isCostCard
                ? undefined
                : (data, event) => {
                    const mode = event.metaKey ? "newTab" : "sameWindow"
                    if (
                      drillDownInto &&
                      data.activePayload &&
                      data.activePayload.length > 0
                    ) {
                      drillDownInto(
                        data.activePayload[0].payload.fullName,
                        mode,
                      )
                    }
                  },
            }}
            style={{
              ...(isCostCard ? { pointerEvents: "none" } : {}),
              ...(isAnimationActive
                ? {}
                : {
                    "& *": {
                      animation: "none !important",
                      transition: "none !important",
                    },
                  }),
            }}
          />
        </>
      )}
    </QueryWrapper>
  )
}
